/**
 * Cáscara del PORTAL PÚBLICO: encabezado con la marca de la federación,
 * navegación, selector de idioma y pie.
 *
 * ## Qué decide este componente
 *
 *  - **Si esta URL es del portal público.** Lo resuelve `usePublicRoute` sobre
 *    el `pathname`; si devuelve `null`, `main.tsx` monta el portal interno de
 *    siempre. Los dos conviven en el mismo bundle y en el mismo origen: no hay
 *    build aparte ni subdominio que aprovisionar, que es lo que hace que
 *    `liga.org/feva` funcione el día uno del despliegue.
 *  - **La marca.** El color de la organización entra por UNA variable CSS
 *    (`--brand`), así FeVA y ACLAV se ven distintas sin una hoja de estilos por
 *    cliente.
 *  - **El `<title>` y el `lang` del documento**, que es lo que ve el que
 *    comparte el link y lo que lee un lector de pantalla.
 *
 * ## El widget no lleva cáscara
 *
 * `/{slug}/widget/court/{matchId}` se monta pelado, sin encabezado ni pie: es
 * un browser source de OBS, no una página. Ese corte se hace acá arriba de
 * todo, antes de dibujar nada.
 */
import { useEffect, useMemo } from 'react';
import { LOCALES, LOCALE_NAMES, getLocale, isLocale, setLocale } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { PublicClient, type PublicOrgBrand } from '../../lib/public-client';
import {
  publicHomePath,
  publicHref,
  publicResultsPath,
  type PublicRoute,
} from '../../lib/public-router';
import { CourtWidget } from './CourtWidget';
import { navigatePublic, useOnce } from './hooks';
import { interceptable } from './parts';
import { PublicCompetition } from './PublicCompetition';
import { PublicHome } from './PublicHome';
import { PublicMatch } from './PublicMatch';
import { PublicResults } from './PublicResults';
import { rememberPublicLocale } from './locale';
import './public.css';

export function PublicApp({
  route,
  base,
  apiUrl = '',
  fetchImpl,
}: {
  route: PublicRoute;
  /** `import.meta.env.BASE_URL` de Vite: `/` en dev, `/portal/` en el deploy. */
  base: string;
  /** Base del API; vacío = mismo origen (el caso del despliegue con Caddy). */
  apiUrl?: string;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
}): JSX.Element {
  const client = useMemo(
    () => new PublicClient({ baseUrl: apiUrl, ...(fetchImpl ? { fetchImpl } : {}) }),
    [apiUrl, fetchImpl],
  );

  // El overlay va pelado: nada de encabezado, nada de pie, fondo transparente.
  if (route.name === 'widget' && route.matchId) {
    return (
      <CourtWidget
        client={client}
        slug={route.slug}
        matchId={route.matchId}
        query={route.query}
      />
    );
  }

  return <PublicShell route={route} base={base} client={client} />;
}

function PublicShell({
  route,
  base,
  client,
}: {
  route: PublicRoute;
  base: string;
  client: PublicClient;
}): JSX.Element {
  const t = useT();
  // La marca se pide UNA vez, por la ruta más chica del portal: el nombre y el
  // color de una federación no cambian durante un partido. Cada pantalla
  // pollea SU documento; el marco de la página no pollea nada.
  const brandState = useOnce(() => client.getOrg(route.slug), [route.slug]);
  const org: PublicOrgBrand | null = brandState.data;

  useEffect(() => {
    if (org === null) return;
    // Lo que ve el que comparte el link y lo que queda en el historial.
    document.title = `${org.shortName ?? org.name} · ${t('publicPortal.title')}`;
  }, [org, t]);

  // El portal no existe: slug inventado, organización deshabilitada o con el
  // portal apagado. El API contesta lo mismo para los tres a propósito.
  const notFound = brandState.error !== null;

  return (
    <div
      className="pub"
      style={
        org?.brandColor
          ? ({ '--brand': org.brandColor } as React.CSSProperties)
          : undefined
      }
    >
      <header className="pub-head">
        <div className="pub-head-inner">
          <a
            className="pub-brand"
            href={publicHref(publicHomePath(route.slug), base)}
            onClick={interceptable(publicHref(publicHomePath(route.slug), base), navigatePublic)}
          >
            {/* Las siglas sobre el color de la organización: la marca se
                reconoce aunque el nombre largo no entre en un teléfono. */}
            <span className="pub-mark" aria-hidden="true">
              {brandMark(org?.shortName ?? null, org?.name ?? route.slug)}
            </span>
            <span className="pub-brand-text">
              <strong>{org?.name ?? route.slug}</strong>
              <small>{t('publicPortal.title')}</small>
            </span>
          </a>

          <nav className="pub-nav">
            <NavLink
              href={publicHref(publicHomePath(route.slug), base)}
              active={route.name === 'home'}
              label={t('publicPortal.today')}
            />
            <NavLink
              href={publicHref(publicResultsPath(route.slug), base)}
              active={route.name === 'results'}
              label={t('publicPortal.results')}
            />
          </nav>

          <LanguagePicker />
        </div>
      </header>

      <main className="pub-main">
        {notFound ? (
          <>
            <h1 className="pub-title">{t('publicPortal.notFoundTitle')}</h1>
            <p className="pub-empty">{t('publicPortal.notFoundBody', { slug: route.slug })}</p>
          </>
        ) : (
          <Page route={route} base={base} client={client} />
        )}
      </main>

      <footer className="pub-foot">
        <div className="pub-foot-inner">
          <span>{org?.name ?? ''}</span>
          <span className="spacer" />
          {/* El portal interno vive en la misma app: el link es para la gente
              de la federación que llegó por la puerta pública. */}
          <a href={publicHref('/', base) || '/'}>{t('publicPortal.staffLink')}</a>
        </div>
      </footer>
    </div>
  );
}

function Page({
  route,
  base,
  client,
}: {
  route: PublicRoute;
  base: string;
  client: PublicClient;
}): JSX.Element {
  const t = useT();
  switch (route.name) {
    case 'home':
      return (
        <PublicHome client={client} slug={route.slug} base={base} onNavigate={navigatePublic} />
      );
    case 'results':
      return (
        <PublicResults client={client} slug={route.slug} base={base} onNavigate={navigatePublic} />
      );
    case 'competition':
      return route.competitionId ? (
        <PublicCompetition
          client={client}
          slug={route.slug}
          base={base}
          competitionId={route.competitionId}
          tab={route.competitionTab ?? 'fixture'}
          onNavigate={navigatePublic}
        />
      ) : (
        <p className="pub-empty">{t('publicPortal.unknownRoute')}</p>
      );
    case 'match':
      return route.matchId ? (
        <PublicMatch
          client={client}
          slug={route.slug}
          base={base}
          matchId={route.matchId}
          onNavigate={navigatePublic}
        />
      ) : (
        <p className="pub-empty">{t('publicPortal.unknownRoute')}</p>
      );
    default:
      return <p className="pub-empty">{t('publicPortal.unknownRoute')}</p>;
  }
}

/**
 * Siglas de la marca: el nombre corto si la organización lo cargó ("FeVA"), o
 * las iniciales de las palabras con mayúscula del nombre largo ("Federación del
 * Voleibol Argentino" → "FVA"). Nunca más de cinco letras.
 */
export function brandMark(shortName: string | null, name: string): string {
  if (shortName !== null && shortName.trim() !== '') return shortName.trim().slice(0, 5);
  const initials = name
    .split(/\s+/)
    .filter((word) => /^\p{Lu}/u.test(word))
    .map((word) => word[0])
    .join('');
  if (initials.length >= 2) return initials.slice(0, 4);
  return name.trim().slice(0, 3).toUpperCase();
}

function NavLink({
  href,
  active,
  label,
}: {
  href: string;
  active: boolean;
  label: string;
}): JSX.Element {
  return (
    <a
      href={href}
      aria-current={active ? 'page' : undefined}
      onClick={interceptable(href, navigatePublic)}
    >
      {label}
    </a>
  );
}

/**
 * Selector de idioma. Va en el encabezado y no escondido en un menú porque el
 * portal público es lo que se le manda a la prensa internacional de un
 * Sudamericano: es una decisión de producto, no una preferencia de usuario
 * frecuente.
 */
function LanguagePicker(): JSX.Element {
  const t = useT();
  const current = getLocale();
  return (
    <div className="pub-lang" role="group" aria-label={t('settings.languageCard')}>
      {LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          aria-pressed={current === locale}
          title={LOCALE_NAMES[locale]}
          onClick={() => {
            if (!isLocale(locale)) return;
            setLocale(locale);
            // La elección del visitante se recuerda en ESTE navegador y aparte
            // de los ajustes del portal interno: el hincha que entra a /feva no
            // comparte preferencias con la máquina de trabajo del estadístico.
            rememberPublicLocale(locale);
          }}
        >
          {locale}
        </button>
      ))}
    </div>
  );
}
